import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  createRewardProgramWithRule,
  getRewardPrograms,
  updateRewardProgramAsync,
} from "../../../actions/rewards";
import { apiClientJSON } from "../../../apis/apiClient";
import * as endpoints from "../../../configs/endpoints";
import { useShowToast } from "../../../hooks";
import useFleetsByPaymentConfig from "../../../hooks/useFleetsByPaymentConfig";
import {
  modifyCurrencyToSymbol,
  modifyCurrencyToSymbolAndValue,
} from "../../../utils/utils";
import ButtonWithLoader from "../../ButtonWithLoader";
import InputController from "../../inputController";
import DateTimePickerController from "../../inputController/DateTimePickerController";
import { FormInputDropdown } from "../../inputController/FormInputDropdown";
import SwitchController from "../../inputController/SwitchController";
import TextAreaController from "../../inputController/TextAreaController";

const RewardProgramModal = ({ visible, onClose, isEdit, programData }) => {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 12,
    p: 4,
    overflowY: "auto",
    maxHeight: "90vh",
    "&::-webkit-scrollbar": {
      WebkitAppearance: "none",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "8px",
      border: "1px solid white",
      backgroundColor: "rgba(0,0,0,.5)",
    },
    "&::-webkit-scrollbar:vertical": {
      width: "8px",
    },
  };

  const dispatch = useDispatch();
  const showToast = useShowToast();
  const {
    fleetsByPaymentConfig,
    fleetCurrencies,
    loading: loadingFleets,
  } = useFleetsByPaymentConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingBonuses, setExistingBonuses] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [priorityError, setPriorityError] = useState(null);

  const {
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      organizationPaymentConfigurationId: "",
      rewardType: "USER_REGISTERED",
      name: "",
      description: "",
      isActive: true,
      startDate: null,
      endDate: null,
      rewardAmount: "",
      displayTitle: "",
      displayMessage: "",
      priority: 1,
    },
  });

  const watchStartDate = watch("startDate");
  const watchFleet = watch("organizationPaymentConfigurationId");

  useEffect(() => {
    if (!visible || !watchFleet || isEdit) {
      setExistingBonuses([]);
      return;
    }

    const fetchExistingBonuses = async () => {
      setLoadingExisting(true);
      try {
        const programsResponse = await apiClientJSON.post(
          endpoints.queryRewardPrograms(),
          {
            filter: {
              payment_configuration_id: watchFleet,
              is_active: true,
            },
          }
        );

        const programs =
          programsResponse.data?.body?.query_response?.entities || [];
        if (programs.length === 0) {
          setExistingBonuses([]);
          setValue("priority", 1);
          setLoadingExisting(false);
          return;
        }

        const programIds = programs.map((p) => p.id);
        const rulesResponse = await apiClientJSON.post(
          endpoints.queryRewardRules(),
          {
            filter: {
              reward_program_ids: programIds,
            },
          }
        );

        const rules = rulesResponse.data?.body?.query_response?.entities || [];

        const bonuses = programs.map((program) => {
          const rule = rules.find((r) => r.reward_program_id === program.id);
          return {
            id: program.id,
            name: program.name,
            priority: rule?.priority || 0,
            amount: rule?.reward_config?.fixed_amount_minor
              ? (rule.reward_config.fixed_amount_minor / 100).toFixed(2)
              : null,
          };
        });

        setExistingBonuses(bonuses);

        const maxPriority = Math.max(...bonuses.map((b) => b.priority), 0);
        setValue("priority", maxPriority + 1);
      } catch (error) {
        console.error("Failed to fetch existing bonuses:", error);
        setExistingBonuses([]);
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExistingBonuses();
  }, [visible, watchFleet, isEdit, setValue]);

  const paymentConfigOptions = useMemo(() => {
    return Object.values(fleetsByPaymentConfig)
      .filter((fleet) => fleet.payment_configuration_id)
      .map((fleet) => {
        const currency = fleetCurrencies[fleet.payment_configuration_id];
        return {
          value: fleet.payment_configuration_id,
          label: currency ? `${fleet.name} (${currency})` : fleet.name,
        };
      });
  }, [fleetsByPaymentConfig, fleetCurrencies]);

  const selectedCurrency = watchFleet ? fleetCurrencies[watchFleet] : null;
  const currencySymbol = selectedCurrency
    ? modifyCurrencyToSymbol(selectedCurrency)
    : null;

  useEffect(() => {
    if (visible && isEdit && programData) {
      reset({
        organizationPaymentConfigurationId:
          programData.organizationPaymentConfigurationId,
        rewardType: "USER_REGISTERED",
        name: programData.name,
        description: programData.description || "",
        isActive: programData.isActive,
        startDate: programData.startDate
          ? new Date(programData.startDate)
          : null,
        endDate: programData.endDate ? new Date(programData.endDate) : null,
        rewardAmount: "",
        displayTitle: "",
        displayMessage: "",
        priority: 1,
      });
    } else if (visible && !isEdit) {
      const defaultPaymentConfigId =
        paymentConfigOptions.length > 0 ? paymentConfigOptions[0].value : "";
      reset({
        organizationPaymentConfigurationId: defaultPaymentConfigId,
        rewardType: "USER_REGISTERED",
        name: "",
        description: "",
        isActive: true,
        startDate: null,
        endDate: null,
        rewardAmount: "",
        displayTitle: "",
        displayMessage: "",
        priority: 1,
      });
    }
  }, [visible, isEdit, programData, reset, paymentConfigOptions]);

  const onSubmit = async (data) => {
    setPriorityError(null);

    if (!isEdit) {
      const priority = parseInt(data.priority);
      const conflicting = existingBonuses.find((b) => b.priority === priority);
      if (conflicting) {
        setPriorityError(
          `Priority ${priority} is already used by "${conflicting.name}". Choose a different priority.`
        );
        return;
      }
    }

    const programPayload = {
      payment_configuration_id: data.organizationPaymentConfigurationId,
      name: data.name,
      description: data.description || null,
      is_active: data.isActive,
      start_datetime: data.startDate
        ? new Date(data.startDate).toISOString()
        : null,
      end_datetime: data.endDate ? new Date(data.endDate).toISOString() : null,
    };

    setIsSubmitting(true);

    if (isEdit && programData) {
      const result = await dispatch(
        updateRewardProgramAsync(programData.id, programPayload)
      );
      setIsSubmitting(false);

      if (result.success) {
        dispatch(getRewardPrograms({ page: 0, size: 1000 }));
        onClose();
      } else {
        showToast("Failed to update program", "error");
      }
    } else {
      const rulePayload = {
        name: `${data.name} - Signup Bonus`,
        trigger_type: "USER_REGISTERED",
        reward_type: "WALLET_CREDIT",
        reward_config: {
          fixed_amount_minor: Math.round(parseFloat(data.rewardAmount) * 100),
        },
        per_user_limit: 1,
        display_title: data.displayTitle || null,
        display_message: data.displayMessage || null,
        priority: parseInt(data.priority),
      };

      const result = await dispatch(
        createRewardProgramWithRule(programPayload, rulePayload)
      );
      setIsSubmitting(false);

      if (result.success) {
        dispatch(getRewardPrograms({ page: 0, size: 1000 }));
        onClose();
      } else {
        showToast("Failed to create signup bonus", "error");
      }
    }
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <FormProvider control={control}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              {isEdit ? "Edit Reward" : "Create Reward"}
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <FormInputDropdown
                name="rewardType"
                options={[
                  { value: "USER_REGISTERED", label: "User Registration" },
                ]}
                errors={errors}
                label="Trigger"
                required={true}
                styles={{
                  width: "100%",
                }}
                disabled={isEdit}
                control={control}
              />
            </Box>

            {!isEdit && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Only one reward of each type applies per user. The highest
                priority reward is used.
              </Alert>
            )}

            <Box sx={{ mb: 1.5 }}>
              {loadingFleets ? (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading fleets...
                  </Typography>
                </Box>
              ) : (
                <FormInputDropdown
                  name="organizationPaymentConfigurationId"
                  options={paymentConfigOptions}
                  errors={errors}
                  label="Fleet"
                  required={true}
                  styles={{
                    width: "100%",
                  }}
                  disabled={isEdit}
                  control={control}
                />
              )}
            </Box>

            {!isEdit && existingBonuses.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  This fleet has {existingBonuses.length} active reward
                  {existingBonuses.length > 1 ? "s" : ""}:
                </Typography>
                <List dense={true} sx={{ py: 0 }}>
                  {existingBonuses.map((bonus) => (
                    <ListItem key={bonus.id} sx={{ py: 0, px: 0 }}>
                      <ListItemText
                        primary={`${bonus.name} - Priority ${bonus.priority}${bonus.amount ? ` (${modifyCurrencyToSymbolAndValue(selectedCurrency, parseFloat(bonus.amount))})` : ""}`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {loadingExisting && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Checking existing rewards...
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="name"
                label="Name"
                required={true}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name="description"
                label="Description"
                required={false}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
            </Box>

            {!isEdit && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1.5, fontWeight: 600, color: "primary.main" }}
                >
                  Reward
                </Typography>

                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name="rewardAmount"
                    label={`Bonus Amount${currencySymbol ? ` (${currencySymbol})` : ""}`}
                    type="number"
                    required={true}
                    styles={{
                      width: "100%",
                    }}
                    control={control}
                    extraRules={{
                      min: {
                        value: 0.01,
                        message: "Amount must be greater than 0",
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={selectedCurrency ? "text.secondary" : "error"}
                  >
                    {selectedCurrency
                      ? `Amount credited to user's wallet when they sign up (${selectedCurrency})`
                      : "Currency not found for selected fleet"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name="priority"
                    label="Priority"
                    type="number"
                    required={true}
                    styles={{
                      width: "100%",
                    }}
                    control={control}
                    extraRules={{
                      min: { value: 1, message: "Priority must be at least 1" },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Higher number = applied first. Priority 10 beats Priority 5.
                  </Typography>
                  {priorityError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {priorityError}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name="displayTitle"
                    label="Title"
                    required={false}
                    styles={{
                      width: "100%",
                    }}
                    control={control}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Title shown to user when they receive the bonus (e.g.,
                    "Welcome Bonus!")
                  </Typography>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <TextAreaController
                    name="displayMessage"
                    label="Message"
                    required={false}
                    multiline={true}
                    styles={{
                      width: "100%",
                    }}
                    control={control}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Message shown to user when they receive the bonus
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1.5, fontWeight: 600, color: "primary.main" }}
                >
                  Schedule
                </Typography>
              </>
            )}

            <Box sx={{ mb: 1.5 }}>
              <SwitchController
                control={control}
                name="isActive"
                errors={errors}
                label="Active"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Only active bonuses are applied to new signups
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <DateTimePickerController
                name="startDate"
                errors={errors}
                required={false}
                label="Start Date & Time (optional)"
                control={control}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <DateTimePickerController
                name="endDate"
                errors={errors}
                required={false}
                label="End Date & Time (optional)"
                control={control}
                extraRules={{
                  validate: (value) => {
                    if (value && watchStartDate) {
                      if (new Date(value) < new Date(watchStartDate)) {
                        return "End date must be after start date";
                      }
                    }
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button variant="outlined" color="error" onClick={onClose}>
                Cancel
              </Button>
              {!isEdit && (
                <ButtonWithLoader text="Create" isLoading={isSubmitting} />
              )}
              {isEdit && (
                <ButtonWithLoader text="Update" isLoading={isSubmitting} />
              )}
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default RewardProgramModal;
